'use client';

/**
 * PostHog Analytics Provider (client-side)
 *
 * Initializes posthog-js ONLY after the user grants analytics cookie consent —
 * on the public marketing site AND inside the authenticated app. Client-side
 * analytics sets cookies/localStorage and drives session replay, so under
 * ePrivacy it requires consent regardless of login (a signed-in account or the
 * Terms/Privacy agreement does not by itself satisfy the cookie-consent rule).
 *
 * The logged-in product-analytics baseline is captured SERVER-SIDE instead (see
 * `@/lib/analytics/server`), which sets no cookies and therefore does not depend
 * on this gate. Net: no client cookies/replay without consent; server-side
 * product events still flow for authenticated usage under legitimate interest.
 *
 * Session replay masks all text and inputs; replay is additionally stopped for
 * student (minor) sessions in `posthog-identify`.
 */

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { useCookieConsent } from '@/components/providers/cookie-consent-provider';

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const { hasConsented, preferences } = useCookieConsent();
  const initializedRef = useRef(false);

  useEffect(() => {
    // Client-side analytics (cookies + session replay) requires explicit
    // analytics consent everywhere, per ePrivacy. Logged-in product usage is
    // captured server-side (no cookies) — see @/lib/analytics/server.
    const analyticsAllowed = hasConsented && preferences.analytics;

    if (!analyticsAllowed) {
      // If PostHog was previously initialized and consent was withdrawn, opt out
      if (initializedRef.current) {
        posthog.opt_out_capturing();
      }
      return;
    }

    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key) return;

    if (!initializedRef.current) {
      posthog.init(key, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        capture_pageview: false,
        persistence: 'localStorage+cookie',
        respect_dnt: true,
        session_recording: {
          // Mask all inputs and on-screen text so replays never capture
          // readable personal/student data (this is a schools product).
          // `maskTextSelector: '*'` masks every text node (posthog-js has no
          // `maskAllText` init option).
          maskAllInputs: true,
          maskTextSelector: '*'
        }
      });
      initializedRef.current = true;
    } else {
      // Re-enable capturing if previously opted out
      posthog.opt_in_capturing();
    }
  }, [hasConsented, preferences.analytics]);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
